import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, MapPin, Eye, Save, RotateCcw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

// Single-value fields a builder maps on their bank form.
const SINGLE_FIELDS = [
  { key: 'borrower', label: 'Borrower' },
  { key: 'address', label: 'Street Address' },
  { key: 'city', label: 'City' },
  { key: 'short_legal', label: 'Short Legal' },
  { key: 'loan_number', label: 'Loan #' },
  { key: 'draw_account', label: 'Draw Account' },
  { key: 'date', label: 'Date Submitted' },
  { key: 'total', label: 'Total Requested' },
];
// Line-item column anchors (first row).
const LINE_FIELDS = [
  { key: 'li_name', label: 'Line: Supplier name' },
  { key: 'li_amount', label: 'Line: Amount' },
  { key: 'li_category', label: 'Line: Category #' },
];
const SIG_FIELD = { key: 'signature', label: 'Signature' };

/**
 * DrawFormMapper — lets the user place data fields onto an uploaded bank draw
 * form (any bank). The form image is rendered at 1 CSS px = 1 PDF point so
 * marker coordinates map straight to PDF points (top-left origin).
 */
export const DrawFormMapper = ({ isOpen, onClose, projectId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);
  const [pageW, setPageW] = useState(612);
  const [pageH, setPageH] = useState(792);
  const [positions, setPositions] = useState({}); // key -> {x,y,font_size,align,width,height}
  const [activeKey, setActiveKey] = useState(null);
  const [rowHeight, setRowHeight] = useState(16.9);
  const [liFontSize, setLiFontSize] = useState(10);
  const [maxRows, setMaxRows] = useState(19);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyPreview, setBusyPreview] = useState(false);
  const imgWrapRef = useRef(null);
  const dragRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [renderRes, mapRes] = await Promise.all([
        apiService.renderProjectDrawForm(projectId),
        apiService.getProjectDrawFormMap(projectId),
      ]);
      setImgSrc(renderRes.data.image);
      setPageW(renderRes.data.page_width);
      setPageH(renderRes.data.page_height);

      const fm = mapRes.data.field_map;
      if (fm) {
        const pos = {};
        Object.entries(fm.fields || {}).forEach(([k, v]) => { pos[k] = { ...v }; });
        const li = fm.line_items || {};
        if (li.name?.x != null) pos.li_name = { x: li.name.x, y: li.start_y, align: li.name.align || 'left' };
        if (li.amount?.x != null) pos.li_amount = { x: li.amount.x, y: li.start_y, align: li.amount.align || 'right' };
        if (li.category?.x != null) pos.li_category = { x: li.category.x, y: li.start_y, align: li.category.align || 'left' };
        setPositions(pos);
        if (li.row_height) setRowHeight(li.row_height);
        if (li.font_size) setLiFontSize(li.font_size);
        if (li.max_rows) setMaxRows(li.max_rows);
      } else {
        setPositions({});
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Could not load the uploaded form. Upload a PDF first.');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [projectId, onClose]);

  useEffect(() => {
    if (isOpen) { loadAll(); }
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [isOpen]);

  const placeAt = (clientX, clientY) => {
    if (!activeKey || !imgWrapRef.current) return;
    const rect = imgWrapRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(pageW, clientX - rect.left));
    const y = Math.max(0, Math.min(pageH, clientY - rect.top));
    setPositions((prev) => {
      const existing = prev[activeKey] || {};
      const base = { font_size: 10, align: 'left', ...existing, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
      if (activeKey === 'signature') { base.width = existing.width || 120; base.height = existing.height || 28; }
      if (activeKey === 'total') base.align = existing.align || 'right';
      if (activeKey === 'li_amount') base.align = existing.align || 'right';
      return { ...prev, [activeKey]: base };
    });
  };

  const handleImgClick = (e) => placeAt(e.clientX, e.clientY);

  const startDrag = (key, e) => {
    e.stopPropagation();
    setActiveKey(key);
    dragRef.current = key;
    const move = (ev) => {
      if (!dragRef.current || !imgWrapRef.current) return;
      const rect = imgWrapRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(pageW, ev.clientX - rect.left));
      const y = Math.max(0, Math.min(pageH, ev.clientY - rect.top));
      setPositions((prev) => ({ ...prev, [dragRef.current]: { ...prev[dragRef.current], x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 } }));
    };
    const up = () => { dragRef.current = null; window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const buildMap = () => {
    const fields = {};
    SINGLE_FIELDS.forEach(({ key }) => {
      const p = positions[key];
      if (p && p.x != null) fields[key] = { x: p.x, y: p.y, font_size: p.font_size || 10, align: p.align || 'left' };
    });
    if (positions.signature?.x != null) {
      const s = positions.signature;
      fields.signature = { x: s.x, y: s.y, width: s.width || 120, height: s.height || 28 };
    }
    const line_items = {};
    const ln = positions.li_name, la = positions.li_amount, lc = positions.li_category;
    const startY = ln?.y ?? la?.y ?? lc?.y;
    if (startY != null) {
      line_items.start_y = startY;
      line_items.row_height = parseFloat(rowHeight) || 16;
      line_items.font_size = parseFloat(liFontSize) || 10;
      line_items.max_rows = parseInt(maxRows, 10) || 19;
      if (ln?.x != null) line_items.name = { x: ln.x, align: 'left' };
      if (la?.x != null) line_items.amount = { x: la.x, align: 'right' };
      if (lc?.x != null) line_items.category = { x: lc.x, align: 'left' };
    }
    return { page_index: 0, page_width: pageW, page_height: pageH, fields, line_items };
  };

  const handlePreview = async () => {
    setBusyPreview(true);
    try {
      const res = await apiService.previewFillDrawForm(projectId, buildMap());
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Could not build preview');
    } finally {
      setBusyPreview(false);
    }
  };

  const handleSave = async () => {
    const map = buildMap();
    if (Object.keys(map.fields).length === 0 && !map.line_items.start_y) {
      toast.error('Place at least one field before saving');
      return;
    }
    setSaving(true);
    try {
      await apiService.saveProjectDrawFormMap(projectId, map);
      toast.success('Field map saved — you can now generate draws on your bank form');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save field map');
    } finally {
      setSaving(false);
    }
  };

  const activePos = activeKey ? positions[activeKey] : null;
  const placedCount = Object.keys(positions).filter((k) => positions[k]?.x != null).length;

  const renderPalette = (items) => items.map(({ key, label }) => {
    const placed = positions[key]?.x != null;
    return (
      <button
        key={key}
        type="button"
        onClick={() => setActiveKey(key)}
        data-testid={`map-field-${key}`}
        className={`text-left text-xs rounded-md border px-2 py-1.5 transition flex items-center justify-between gap-1 ${activeKey === key ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-400' : placed ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
      >
        <span className="text-slate-700 truncate">{label}</span>
        {placed ? <span className="text-emerald-600 text-[10px] font-semibold">placed</span> : <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />}
      </button>
    );
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[1000px] max-h-[94vh] flex flex-col p-0" data-testid="draw-form-mapper">
        <DialogHeader className="p-4 border-b">
          <DialogTitle>Map your bank form fields</DialogTitle>
          <DialogDescription>
            Pick a field on the right, then click on the form where its value should print. Drag any marker to fine-tune. Map once — it&apos;s saved for future draws.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-16">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[1fr_300px] gap-0">
            {/* Form canvas */}
            <div className="overflow-auto bg-slate-100 p-4 max-h-[74vh]">
              <div
                ref={imgWrapRef}
                onClick={handleImgClick}
                className={`relative mx-auto shadow-lg bg-white ${activeKey ? 'cursor-crosshair' : ''}`}
                style={{ width: pageW, height: pageH }}
                data-testid="map-canvas"
              >
                {imgSrc && <img src={imgSrc} alt="Bank form" draggable={false} style={{ width: pageW, height: pageH, userSelect: 'none' }} />}
                {Object.entries(positions).map(([key, p]) => {
                  if (p?.x == null) return null;
                  const isSig = key === 'signature';
                  return (
                    <div
                      key={key}
                      onMouseDown={(e) => startDrag(key, e)}
                      onClick={(e) => { e.stopPropagation(); setActiveKey(key); }}
                      title={key}
                      className={`absolute flex items-center ${activeKey === key ? 'z-20' : 'z-10'}`}
                      style={{ left: p.x, top: p.y, transform: 'translateY(-100%)', cursor: 'move' }}
                    >
                      {isSig ? (
                        <div className="border-2 border-dashed border-purple-500 bg-purple-500/10" style={{ width: p.width || 120, height: p.height || 28, transform: 'translateY(50%)' }} />
                      ) : (
                        <>
                          <div className={`w-2 h-2 rounded-full ${activeKey === key ? 'bg-blue-600' : 'bg-red-500'}`} />
                          <span className={`ml-0.5 px-1 rounded text-[9px] font-semibold whitespace-nowrap ${activeKey === key ? 'bg-blue-600 text-white' : 'bg-red-500 text-white'}`}>{key}</span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Controls */}
            <div className="border-l overflow-y-auto p-3 space-y-3 max-h-[74vh]">
              <div className="text-[11px] text-slate-500 flex items-start gap-1 bg-slate-50 rounded-md p-2">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>Click a field, then click the form line where the value should sit. {placedCount} placed.</span>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Header & totals</p>
                <div className="grid grid-cols-2 gap-1.5">{renderPalette(SINGLE_FIELDS)}</div>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Line items (first row)</p>
                <div className="grid grid-cols-1 gap-1.5">{renderPalette(LINE_FIELDS)}</div>
                <div className="grid grid-cols-3 gap-1.5 mt-2">
                  <div>
                    <Label className="text-[10px] text-slate-500">Row height</Label>
                    <Input type="number" step="0.1" value={rowHeight} onChange={(e) => setRowHeight(e.target.value)} className="h-7 text-xs" data-testid="map-row-height" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Font</Label>
                    <Input type="number" step="0.5" value={liFontSize} onChange={(e) => setLiFontSize(e.target.value)} className="h-7 text-xs" data-testid="map-li-font" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Max rows</Label>
                    <Input type="number" value={maxRows} onChange={(e) => setMaxRows(e.target.value)} className="h-7 text-xs" data-testid="map-max-rows" />
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Signature (optional)</p>
                <div className="grid grid-cols-1 gap-1.5">{renderPalette([SIG_FIELD])}</div>
              </div>

              {/* Selected field tuning */}
              {activePos && activeKey !== 'signature' && !activeKey.startsWith('li_') && (
                <div className="rounded-md border border-slate-200 p-2 space-y-2">
                  <p className="text-[11px] font-semibold text-slate-700">Adjust “{activeKey}”</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div>
                      <Label className="text-[10px] text-slate-500">Font size</Label>
                      <Input type="number" step="0.5" value={activePos.font_size || 10} onChange={(e) => setPositions((prev) => ({ ...prev, [activeKey]: { ...prev[activeKey], font_size: parseFloat(e.target.value) || 10 } }))} className="h-7 text-xs" data-testid="map-field-font" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500">Align</Label>
                      <select value={activePos.align || 'left'} onChange={(e) => setPositions((prev) => ({ ...prev, [activeKey]: { ...prev[activeKey], align: e.target.value } }))} className="h-7 text-xs w-full border border-slate-200 rounded-md px-1" data-testid="map-field-align">
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-6 text-[11px] text-red-600 px-1" onClick={() => setPositions((prev) => { const n = { ...prev }; delete n[activeKey]; return n; })}>Remove marker</Button>
                </div>
              )}
              {activePos && activeKey === 'signature' && (
                <div className="rounded-md border border-slate-200 p-2 space-y-2">
                  <p className="text-[11px] font-semibold text-slate-700">Signature box size (pts)</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div>
                      <Label className="text-[10px] text-slate-500">Width</Label>
                      <Input type="number" value={activePos.width || 120} onChange={(e) => setPositions((prev) => ({ ...prev, signature: { ...prev.signature, width: parseFloat(e.target.value) || 120 } }))} className="h-7 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[10px] text-slate-500">Height</Label>
                      <Input type="number" value={activePos.height || 28} onChange={(e) => setPositions((prev) => ({ ...prev, signature: { ...prev.signature, height: parseFloat(e.target.value) || 28 } }))} className="h-7 text-xs" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="p-3 border-t bg-slate-50 gap-2">
          <Button variant="ghost" onClick={() => setPositions({})} disabled={loading} className="text-slate-500" data-testid="map-clear-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Clear
          </Button>
          <Button variant="outline" onClick={handlePreview} disabled={loading || busyPreview} data-testid="map-preview-btn">
            {busyPreview ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />} Preview with sample data
          </Button>
          <Button onClick={handleSave} disabled={loading || saving} className="bg-blue-600 hover:bg-blue-700" data-testid="map-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save map
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DrawFormMapper;
